import type { GraphModification, ConceptEvent, Graph, Terminology } from "@lr-notitia/notitia-designer/schemas";
import { UserRole } from "@lr-notitia/notitia-designer/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { handleGraphModification } from "./utils";
import { useDomainStore } from "../domain";
import { useGraphStore } from "../graph";
import { useSymbolsStore } from "../symbols";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	symbolsStore = useSymbolsStore();
}

export const conceptEvents = (graph: Ref<Graph>) =>
	computed<ConceptEvent[]>(() => {
		if ( !symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "event" && node.layer === "concept"
			) as ConceptEvent[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase())
		);
	});
export function conceptEvent(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		add: (refersTo: string, origin: string, localTerminology: Terminology) =>
			add(domainId, graph, refersTo, origin, localTerminology),
		update: (event: ConceptEvent) => update(domainId, graph, event),
		remove: (id: string) => remove(domainId, graph, id),
	};
}

function byId(id: string, graph: Ref<Graph>): ConceptEvent | undefined {
	let hit = graph.value.nodes[id];
	return hit && hit.type === "event" && hit.layer === "concept" ? (hit as ConceptEvent) : undefined;
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	refersTo: string,
	origin: string,
	localTerminology: Terminology
): Promise<GraphModification> {
	if (!domainStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		api()
			.domain(domainId.value)
			.concept()
			.events()
			.post({ refersTo, origin, localTerminology })
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, event: ConceptEvent): Promise<GraphModification> {
	if (!graphStore || !domainStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		if (!graph.value.nodes[event.id]) return reject();
		api()
			.domain(domainId.value)
			.concept()
			.event(event.id)
			.put(event)
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.concept()
			.event(id)
			.del()
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

